// Analytics logging utilities for Next.js
import type { User } from '@/services/api';
import { CONFIG } from '@/utils/config';
import { getSessionId } from './sessionHelpers';

export interface LogEventMetadata {
    [key: string]: unknown;
}

export interface QueuedEvent {
    user_id: string | null;
    event_type: string;
    session_id: string;
    timestamp: number;
    environment: string;
    metadata: LogEventMetadata & {
        client_timestamp: number;
    };
    retryCount?: number;
}

/**
 * Detects the environment based on URL and configuration
 * Falls back to CONFIG.env if running server-side
 */
function detectEnvironment(): string {
    const hostname = window.location.hostname;

    // Local development
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
        return 'local';
    }

    // Non-production environments (centralized pattern from Pulumi configs)
    // All non-prod environments use: *.npr.secondsense.co
    if (hostname.includes('.npr.secondsense.co')) {
        return 'development';
    }

    // Production environment
    return 'production';
}

export interface EventBatch {
    events: QueuedEvent[];
}

export interface BatchResponse {
    status: string;
    processed: number;
    bot_detected: boolean;
}

// Queue configuration
const BATCH_SIZE = 20;
const BATCH_TIMEOUT = 5000; // 5 seconds
const MAX_RETRIES = 2;
const STORAGE_KEY = 'analytics_queue';

/**
 * Thread-safe event queue with persistence and proper synchronization
 */
class AnalyticsEventQueue {
    private queue: QueuedEvent[] = [];
    private batchTimer: NodeJS.Timeout | null = null;
    private processingPromise: Promise<void> | null = null;
    private isDestroyed = false;

    constructor() {
        this.loadPersistedEvents();
        this.setupUnloadHandler();
    }

    /**
     * Add event to queue with automatic processing
     */
    addEvent(event: QueuedEvent): void {
        if (this.isDestroyed) return;

        this.queue.push(event);
        this.persistQueue();

        if (this.queue.length >= BATCH_SIZE) {
            void this.processQueue();
        } else {
            this.scheduleNextBatch();
        }
    }

    /**
     * Process queue with proper synchronization
     */
    async processQueue(): Promise<void> {
        // If already processing, wait for current processing to complete
        if (this.processingPromise) {
            return this.processingPromise;
        }

        if (this.queue.length === 0 || this.isDestroyed) {
            return;
        }

        // Create new processing promise
        this.processingPromise = this.doProcessQueue();

        try {
            await this.processingPromise;
        } finally {
            this.processingPromise = null;
        }
    }

    /**
     * Internal queue processing logic
     */
    private async doProcessQueue(): Promise<void> {
        this.clearTimer();

        while (this.queue.length > 0 && !this.isDestroyed) {
            const batch = this.queue.splice(0, BATCH_SIZE);

            try {
                await this.sendBatch(batch);
                this.persistQueue();
            } catch (error) {
                console.warn('Failed to send analytics batch:', error);

                // Re-queue retriable events at the front
                const retriableEvents = batch
                    .filter(event => (event.retryCount || 0) < MAX_RETRIES)
                    .map(event => ({
                        ...event,
                        retryCount: (event.retryCount || 0) + 1,
                    }));

                if (retriableEvents.length > 0) {
                    this.queue.unshift(...retriableEvents);
                    this.persistQueue();

                    // Wait before retrying
                    await this.delay(Math.pow(2, retriableEvents[0].retryCount || 1) * 1000);
                }
                break; // Exit retry loop on error
            }
        }

        // Schedule next batch if more events remain
        if (this.queue.length > 0 && !this.isDestroyed) {
            this.scheduleNextBatch();
        }
    }

    /**
     * Send batch to server
     */
    private async sendBatch(events: QueuedEvent[]): Promise<BatchResponse> {
        const eventBatch: EventBatch = { events };

        const response = await fetch(`${CONFIG.apiUrl}/log-events-batch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventBatch),
            keepalive: true,
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json() as Promise<BatchResponse>;
    }

    /**
     * Schedule next batch processing
     */
    private scheduleNextBatch(): void {
        if (this.batchTimer || this.isDestroyed) return;

        this.batchTimer = setTimeout(() => {
            void this.processQueue();
        }, BATCH_TIMEOUT);
    }

    /**
     * Clear batch timer
     */
    private clearTimer(): void {
        if (this.batchTimer) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }
    }

    /**
     * Persist queue to localStorage
     */
    private persistQueue(): void {
        if (typeof window === "undefined") return;

        try {
            const queueData = {
                events: this.queue,
                timestamp: Date.now(),
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(queueData));
        } catch (error) {
            console.warn('Failed to persist analytics queue:', error);
        }
    }

    /**
     * Load persisted events from localStorage
     */
    private loadPersistedEvents(): void {
        if (typeof window === "undefined") return;

        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) return;

            const queueData = JSON.parse(stored);
            const age = Date.now() - queueData.timestamp;

            // Only restore events less than 1 hour old
            if (age < 3600000 && Array.isArray(queueData.events)) {
                this.queue = queueData.events;
                console.log(`Restored ${this.queue.length} persisted analytics events`);
            }

            localStorage.removeItem(STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to load persisted analytics events:', error);
        }
    }

    /**
     * Setup beforeunload handler for persistence
     */
    private setupUnloadHandler(): void {
        if (typeof window === "undefined") return;

        const handleUnload = () => {
            this.persistQueue();
            void this.processQueue(); // Best effort flush
        };

        window.addEventListener('beforeunload', handleUnload);
        window.addEventListener('pagehide', handleUnload);
    }

    /**
     * Force flush all pending events
     */
    async flush(): Promise<void> {
        if (this.queue.length > 0) {
            await this.processQueue();
        }
    }

    /**
     * Get queue status for debugging
     */
    getStatus(): {
        queueLength: number;
        isProcessing: boolean;
        hasPendingTimer: boolean;
    } {
        return {
            queueLength: this.queue.length,
            isProcessing: this.processingPromise !== null,
            hasPendingTimer: this.batchTimer !== null,
        };
    }

    /**
     * Utility delay function
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Cleanup resources
     */
    destroy(): void {
        this.isDestroyed = true;
        this.clearTimer();
        this.persistQueue();
    }
}

// Global queue instance
const eventQueue = new AnalyticsEventQueue();

/**
 * Log an event to the backend analytics system with batching
 */
export function logEvent(
    user: User | null,
    eventType: string,
    metadata: LogEventMetadata = {}
): void {
    if (typeof window === "undefined") return;

    try {
        const event: QueuedEvent = {
            user_id: user?._id || null,
            event_type: eventType,
            session_id: getSessionId(),
            timestamp: Date.now(),
            environment: detectEnvironment(),
            metadata: {
                ...metadata,
                client_timestamp: Date.now(),
            },
        };

        eventQueue.addEvent(event);
    } catch (error) {
        console.warn("Failed to log event:", error);
    }
}

/**
 * Force flush the event queue
 */
export async function flushEvents(): Promise<void> {
    await eventQueue.flush();
}

/**
 * Get current queue status for debugging
 */
export function getQueueStatus(): {
    queueLength: number;
    isProcessing: boolean;
    hasPendingTimer: boolean;
} {
    return eventQueue.getStatus();
}

/**
 * Track an event (alias for logEvent)
 */
export function trackEvent(
    eventType: string,
    user: User | null,
    metadata: LogEventMetadata = {}
): void {
    logEvent(user, eventType, metadata);
}